import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchSubscriptionAccess } from '../services/subscriptionService';
import { useAuth } from '../contexts/AuthContext';
import { TENANT_ORG_ID_STORAGE_KEY } from '../constants/tenant';
import { isSuperAdminUser, isPlatformOnlySuperAdmin } from '../utils/platformRoles';

function normalizeModuleCodes(raw) {
  if (!raw || !Array.isArray(raw) || raw.length === 0) return ['base'];
  return raw.includes('base') ? raw : ['base', ...raw];
}

/**
 * Módulos pagos + flag de bloqueio (SaaS / Asaas). Alinhado ao condo-platform.
 */
export function useSubscriptionAccess() {
  const { user, booting: authLoading } = useAuth();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!user) {
      setAccess(null);
      setLoading(false);
      return;
    }
    if (isPlatformOnlySuperAdmin(user)) {
      setAccess({ billing_status: 'platform_only', module_codes: ['base'], blocked: false });
      setLoading(false);
      return;
    }
    if (isSuperAdminUser(user)) {
      setAccess({ billing_status: 'super_admin', module_codes: null, blocked: false });
      setLoading(false);
      return;
    }
    const org = (() => {
      try {
        return localStorage.getItem(TENANT_ORG_ID_STORAGE_KEY);
      } catch {
        return null;
      }
    })();
    if (!org) {
      setAccess({ billing_status: 'no_tenant', module_codes: ['base'], blocked: false });
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchSubscriptionAccess();
      setAccess(data);
      setError(null);
    } catch (e) {
      setError(e);
      // Sem o serviço de assinatura, não quebra o app: tudo visível
      setAccess({ billing_status: 'unknown', module_codes: null, blocked: false });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, user, load]);

  const moduleSet = useMemo(() => {
    if (!access) return null;
    if (access.billing_status === 'platform_only') {
      return new Set(['base']);
    }
    if (access.billing_status === 'super_admin') return null;
    if (access.blocked) return new Set();
    const codes = normalizeModuleCodes(access.module_codes);
    return new Set(codes.map((c) => String(c).toLowerCase()));
  }, [access]);

  const blocked = !!access?.blocked;

  return {
    access,
    moduleSet,
    blocked,
    loading: loading || authLoading,
    error,
    reload: load,
  };
}
